import React, {
  useContext,
  useState,
  useEffect,
} from 'react';
import { autorun } from 'mobx';
import { compose } from 'recompose';
import { observer } from 'mobx-react';

import AnnouncementContext from '../../../../context/AnnouncementContext';
import { AnnouncementPublishTab } from '../Announcements/utils';
import LoadingPage from '../../../../../components/LoadingPage';
import LoadingBadge from '../../../../../components/LoadingBadge';
import { ANNOUNCEMENT_DATE_RANGE, buildAnnouncementFeed } from '../../feedUtils';

import AnnouncementItem from './components/AnnouncementItem';
import {
  Container,
  EmptyContainer,
  LoadingMore,
} from './styles';
import texts from './texts.json';

const AnnouncementList = () => {
  const {
    isPostsReady,
    announcementCollection,
    selectedPublishTab,
    scheduledAnnouncementCollection,
    isLoadingPosts,
    selectedDateRange,
    selectedCommunityFilter,
    hidePinnedPosts,
    showCoachPostsOnly,
  } = useContext(AnnouncementContext);

  const isScheduledTab = selectedPublishTab === AnnouncementPublishTab.SCHEDULED;

  const [announcementGroups, setAnnouncementGroups] = useState([]);
  const [hasHiddenPinnedPosts, setHasHiddenPinnedPosts] = useState(false);
  const [hasHiddenClientPosts, setHasHiddenClientPosts] = useState(false);

  // Rebuild the display entries whenever the observed collections or the filters change.
  useEffect(() => {
    const disposer = autorun(() => {
      const docs = isScheduledTab
        ? scheduledAnnouncementCollection.docs || []
        : announcementCollection.docs || [];

      // The scheduled tab is fetched by publishAt asc and means to show that order, so grouping
      // keeps it; siblings share a publishAt, so a bucket lands where its post belongs.
      const groups = buildAnnouncementFeed(docs, {
        communityFilter: selectedCommunityFilter,
        preserveOrder: isScheduledTab,
      });

      // Filter after grouping, so toggling either never changes an aggregate's counts. Neither toggle
      // is rendered on the scheduled tab: those posts are all the coach's and none is pinned yet.
      // Author first, then pins, so a hidden pinned post counts as a coach post hidden by the pin
      // toggle rather than as the feed having no coach posts.
      const isCoachFilterActive = showCoachPostsOnly && !isScheduledTab;
      const coachGroups = isCoachFilterActive
        ? groups.filter((group) => group.base.isByCoach)
        : groups;

      const isPinFilterActive = hidePinnedPosts && !isScheduledTab;
      const visibleGroups = isPinFilterActive
        ? coachGroups.filter((group) => !group.isPinnedGroup)
        : coachGroups;

      setAnnouncementGroups(visibleGroups);
      setHasHiddenClientPosts(coachGroups.length < groups.length);
      setHasHiddenPinnedPosts(visibleGroups.length < coachGroups.length);
    });

    return disposer;
  }, [
    announcementCollection,
    scheduledAnnouncementCollection,
    selectedCommunityFilter,
    isScheduledTab,
    hidePinnedPosts,
    showCoachPostsOnly,
  ]);

  if (!isPostsReady) {
    return (
      <LoadingPage />
    );
  }

  if (!isLoadingPosts && announcementGroups.length === 0) {
    let emptyMessage = texts.noAnnouncements;
    if (isScheduledTab) {
      emptyMessage = texts.noScheduledAnnouncements;
    } else if (hasHiddenPinnedPosts) {
      // The toggle emptied the feed, so a longer range wouldn't help - pinned posts ignore it.
      emptyMessage = texts.onlyPinnedPosts;
    } else if (hasHiddenClientPosts) {
      emptyMessage = texts.onlyClientPosts;
    } else if (selectedDateRange !== ANNOUNCEMENT_DATE_RANGE.ALL) {
      emptyMessage = texts.noPostsInRange;
    }
    return (
      <Container $isReady={isPostsReady}>
        <EmptyContainer>
          {emptyMessage}
        </EmptyContainer>
      </Container>
    );
  }

  return (
    <Container $isReady={isPostsReady}>
      {announcementGroups.map((group) => (
        <AnnouncementItem key={group.key} announcement={group.base} group={group} />
      ))}
      {isLoadingPosts && (
        <LoadingMore>
          <LoadingBadge />
        </LoadingMore>
      )}
    </Container>
  );
};

export default compose(
  observer,
)(AnnouncementList);
